import React, { useEffect, useState } from 'react';
import { Icon, Platform } from './Icons.jsx';
import { NAV } from '../data/mockData.js';
import { api } from '../services/api/client.js';

// ============ SIDEBAR GROUP (expandable parent w/ children) ============
const SidebarGroup = ({ item, current, onNavigate, iconsOnly, onExpand }) => {
  const childActive = item.children?.some(child => child.id === current);
  const [open, setOpen] = useState(childActive);

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  const handleParentClick = () => {
    if (iconsOnly) {
      onExpand?.();
      setOpen(true);
      return;
    }
    setOpen(prev => !prev);
  };

  return (
    <>
      <button
        className={'sb-item sb-group-trigger ' + (childActive ? 'active' : '')}
        onClick={handleParentClick}
        title={iconsOnly ? item.label : ''}
        data-tooltip={iconsOnly ? item.label : undefined}
        aria-label={iconsOnly ? item.label : undefined}
        aria-expanded={iconsOnly ? undefined : open}
      >
        <Icon name={item.icon} size={17} stroke={1.7} />
        {!iconsOnly && <span className="sidebar-item-label">{item.label}</span>}
        {!iconsOnly && (
          <Icon name={open ? 'chev_d' : 'chev_r'} size={12} className="sb-group-chev" />
        )}
      </button>
      {!iconsOnly && open && (
        <div className="sb-group-children">
          {item.children.map(child => (
            <button
              key={child.id}
              className={'sb-item sb-child-item ' + (current === child.id ? 'active' : '')}
              onClick={() => onNavigate(child.id)}
              style={{ paddingLeft: 28 }}
            >
              <Icon name={child.icon} size={15} stroke={1.6} />
              <span className="sidebar-item-label">{child.label}</span>
              {current === child.id && <span className="sb-bullet"/>}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

// ============ SIDEBAR ============
export const Sidebar = ({ current, onNavigate, collapsed, onToggle, onExpand, user, onLogout, mobileOpen, onMobileClose }) => {
  const iconsOnly = collapsed;
  const displayName = user?.name || user?.email || 'Dathent user';
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || 'DU';
  const [postQuota, setPostQuota] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function loadPlanUsage() {
      try {
        const response = await api.billing.getEntitlements();
        if (!cancelled) {
          setPostQuota(response.usage?.publish_ready_posts_monthly || response.usage?.ai_generations_monthly || null);
        }
      } catch {
        if (!cancelled) setPostQuota(null);
      }
    }
    loadPlanUsage();
    return () => {
      cancelled = true;
    };
  }, []);

  const postsUsed = postQuota?.used ?? 0;
  const postsLimit = postQuota?.limit;
  const postsPercent = postsLimit ? Math.min(100, Math.round((postsUsed / postsLimit) * 100)) : 0;
  const postsLabel = postsLimit === null ? `${postsUsed} used` : postQuota ? `${postsUsed} / ${postsLimit}` : 'Loading';
  const expandCollapsedSidebar = (event) => {
    if (!iconsOnly) return;
    if (event.target.closest('.sb-item, .sb-user, .credits-card, .mobile-drawer-close')) return;
    onExpand?.();
  };
  return (
    <aside className="sidebar desktop-sidebar mobile-drawer" aria-hidden={mobileOpen ? 'false' : undefined} onClick={expandCollapsedSidebar}>
      <div className="sb-brand sidebar-header desktop-sidebar-header">
        <div className="sb-logo">
          <img src="/logo.png" alt="Dathent" />
        </div>
        {!iconsOnly && (
          <div className="sb-brand-text sidebar-logo-text">
            <div className="sb-name sidebar-subtitle">Content</div>
            <div className="sb-name-2 sidebar-title">Autopilot</div>
          </div>
        )}
        {!iconsOnly && (
          <button
            className="sb-toggle sidebar-collapse-button"
            onClick={onToggle}
            title="Collapse sidebar"
          >
            <Icon name="chev_l" size={13}/>
          </button>
        )}
        <button className="mobile-drawer-close" onClick={onMobileClose} aria-label="Close navigation">
          <Icon name="x" size={18}/>
        </button>
      </div>

      <nav className="sb-nav sidebar-main-nav">
        {NAV.map(item => (
          item.children
            ? <SidebarGroup key={item.id} item={item} current={current} onNavigate={onNavigate} iconsOnly={iconsOnly} onExpand={onExpand} />
            : (
              <button
                key={item.id}
                className={'sb-item ' + (current === item.id ? 'active' : '')}
                onClick={() => onNavigate(item.id)}
                title={iconsOnly ? item.label : ''}
                data-tooltip={iconsOnly ? item.label : undefined}
                aria-label={iconsOnly ? item.label : undefined}
              >
                <Icon name={item.icon} size={17} stroke={1.7} />
                {!iconsOnly && <span className="sidebar-item-label">{item.label}</span>}
                {!iconsOnly && current === item.id && <span className="sb-bullet"/>}
              </button>
            )
        ))}
      </nav>

      <div className="sb-foot sidebar-bottom">
        <div className="credits-card" title={iconsOnly ? 'Posts left' : undefined}>
          {!iconsOnly && (
            <div className="credits-card-expanded">
              <div className="row between">
                <span className="section-eyebrow">Posts this month</span>
                <Icon name="sparkles" size={12}/>
              </div>
              <div className="credits-num"><b>{postsLabel}</b></div>
              <div className="credits-bar">
                <div className="credits-fill" style={{ width: `${postsLimit === null ? 100 : postsPercent}%` }}/>
              </div>
              <div className="muted" style={{fontSize:11, marginTop:6}}>Publish-ready output quota</div>
            </div>
          )}
          {iconsOnly && <div className="credits-card-collapsed"><Icon name="sparkles" size={14}/><span>{postQuota ? postsUsed : '...'}</span></div>}
        </div>

        <button className={'sb-user ' + (iconsOnly ? 'compact' : '')} onClick={() => onNavigate('brand')}>
          <div className="avatar">{initials}</div>
          {!iconsOnly && <div className="grow" style={{textAlign:'left'}}>
            <div className="account-name" style={{fontWeight:600, fontSize:13}}>{displayName}</div>
            <div className="muted account-subtitle" style={{fontSize:11}}>Dathent account</div>
          </div>}
          {!iconsOnly && <Icon name="chev_r" size={14}/>}
        </button>
        {!iconsOnly && onLogout && (
          <button className="btn btn-ghost btn-sm" style={{width:'100%', marginTop:8}} onClick={onLogout}>
            <span className="sign-out-label">Sign out</span>
          </button>
        )}
      </div>
    </aside>
  );
};

// ============ TOPBAR ============
export const Topbar = ({ title, subtitle, actions, breadcrumbs, onMobileMenu, onLogoClick, navigationMode = 'menu', onBack }) => (
  <header className="topbar desktop-page-header mobile-header">
    <button className="mobile-menu-trigger" onClick={navigationMode === 'back' ? onBack : onMobileMenu} aria-label={navigationMode === 'back' ? 'Go back' : 'Open navigation'}>
      <Icon name={navigationMode === 'back' ? 'chev_l' : 'menu'} size={18}/>
    </button>
    <button className="topbar-logo page-header-logo" onClick={onLogoClick} aria-label="Home">
      <img src="/logo.png" alt="Dathent" height="24" style={{height:24, display:'block', objectFit:'contain', maxWidth:120}}/>
    </button>
    <div className="grow desktop-page-title-block" style={{minWidth:0}}>
      {breadcrumbs && <div className="row" style={{gap:6, marginBottom:4}}>
        {breadcrumbs.map((b, i) => (
          <React.Fragment key={i}>
            {i > 0 && <Icon name="chev_r" size={12}/>}
            <span className={i === breadcrumbs.length - 1 ? '' : 'subtle'} style={{fontSize:12}}>{b}</span>
          </React.Fragment>
        ))}
      </div>}
      <h1 className="topbar-title desktop-page-title mobile-header__title">{title}</h1>
      {subtitle && <div className="topbar-sub desktop-page-subtitle">{subtitle}</div>}
    </div>
    <div className="topbar-actions desktop-page-header-actions mobile-header__actions">{actions}</div>
  </header>
);
